import React, { useCallback, useState, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, useTheme } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

interface FileDropZoneProps {
  onFilesDropped: (files: File[]) => void | Promise<void>;
  uploading: boolean;
  accept?: string;
  enableClipboardPaste?: boolean;
  onClipboardPasteError?: (message: string) => void;
  'data-testid'?: string;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFilesDropped,
  uploading,
  accept,
  enableClipboardPaste = false,
  onClipboardPasteError,
  'data-testid': dataTestId,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [readingClipboard, setReadingClipboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const accent = theme.palette.primary.main;
  const borderColor = dragActive ? accent : theme.palette.divider;
  const bgColor = dragActive
    ? (isDark ? `${accent}15` : `${accent}08`)
    : 'transparent';

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (uploading) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFilesDropped(files);
      }
    },
    [onFilesDropped, uploading]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesDropped(Array.from(e.target.files));
        /* Reset input so the same file can be uploaded again */
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    },
    [onFilesDropped]
  );

  const normalizeClipboardFile = useCallback((blob: Blob): File => {
    const mimeType = blob.type || 'image/png';
    const extension = mimeType.split('/')[1] || 'png';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `clipboard-screenshot-${timestamp}.${extension}`;
    return new File([blob], filename, {
      type: mimeType,
      lastModified: Date.now(),
    });
  }, []);

  const handlePasteFromClipboard = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (uploading || readingClipboard) return;

      if (!navigator.clipboard || !navigator.clipboard.read) {
        onClipboardPasteError?.('Zwischenablage-Import wird von diesem Browser nicht unterstützt.');
        return;
      }

      try {
        setReadingClipboard(true);
        const clipboardItems = await navigator.clipboard.read();

        for (const item of clipboardItems) {
          const imageType = item.types.find((type) => type.startsWith('image/'));
          if (!imageType) continue;
          const imageBlob = await item.getType(imageType);
          const file = normalizeClipboardFile(imageBlob);
          await onFilesDropped([file]);
          return;
        }

        onClipboardPasteError?.('Kein Bild in der Zwischenablage gefunden.');
      } catch {
        onClipboardPasteError?.('Zwischenablage konnte nicht gelesen werden.');
      } finally {
        setReadingClipboard(false);
      }
    },
    [normalizeClipboardFile, onClipboardPasteError, onFilesDropped, readingClipboard, uploading]
  );

  return (
    <Box
      data-testid={dataTestId}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        border: `2px dashed ${borderColor}`,
        borderRadius: 3,
        p: 4,
        textAlign: 'center',
        backgroundColor: bgColor,
        transition: 'all 0.2s ease',
        cursor: uploading ? 'wait' : 'pointer',
        '&:hover': uploading
          ? {}
          : {
              borderColor: accent,
              backgroundColor: isDark ? `${accent}10` : `${accent}05`,
            },
      }}
      onClick={() => {
        if (!uploading && inputRef.current) {
          inputRef.current.click();
        }
      }}
    >
      <input
        data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        ref={inputRef}
        type="file"
        hidden
        multiple
        accept={accept}
        onChange={handleFileSelect}
      />

      {uploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary">
            Wird hochgeladen...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: dragActive ? accent : theme.palette.text.secondary,
              transition: 'color 0.2s',
            }}
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {dragActive ? 'Dateien hier ablegen' : 'Dateien hierher ziehen'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            oder klicken zum Auswaehlen
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            Dateien auswählen
          </Button>
          {enableClipboardPaste && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                readingClipboard ? <CircularProgress size={14} color="inherit" /> : <ContentPasteIcon />
              }
              sx={{ mt: 1 }}
              disabled={uploading || readingClipboard}
              onClick={handlePasteFromClipboard}
            >
              {readingClipboard ? 'Zwischenablage wird gelesen...' : 'Aus Zwischenablage einfügen'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileDropZone;
